import type { AppointmentStatus, CallOutcomeCode, CallStatus } from '../api/types';

// Anzeige immer in der Zeitzone der Projekte (Deutschland).
export const timeZone = 'Europe/Berlin';

const dateTime = new Intl.DateTimeFormat('de-DE', { timeZone, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const dateOnly = new Intl.DateTimeFormat('de-DE', { timeZone, weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
const timeOnly = new Intl.DateTimeFormat('de-DE', { timeZone, hour: '2-digit', minute: '2-digit' });
const isoDate = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });

export const fmtDateTime = (v?: string | null) => (v ? dateTime.format(new Date(v)) : '–');
export const fmtDate = (v?: string | Date | null) => (v ? dateOnly.format(typeof v === 'string' ? new Date(v) : v) : '–');
export const fmtTime = (v?: string | null) => (v ? timeOnly.format(new Date(v)) : '–');

// localDate liefert JJJJ-MM-TT in Europe/Berlin.
export const localDate = (d: Date = new Date()) => isoDate.format(d);

// berlinOffset ermittelt den UTC-Versatz (Minuten) von Europe/Berlin für einen Zeitpunkt.
function berlinOffset(utc: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(utc);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return Math.round((asUTC - utc.getTime()) / 60000);
}

// fromLocal wandelt Datum + Uhrzeit (Europe/Berlin) in einen ISO-Zeitpunkt (UTC) um.
export function fromLocal(date: string, time: string): string {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  const [hh = 0, mm = 0] = time.split(':').map(Number);
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const offset = berlinOffset(guess);
  const result = new Date(guess.getTime() - offset * 60000);
  // Zweiter Durchlauf für Zeitumstellungen.
  const corrected = berlinOffset(result);
  return new Date(guess.getTime() - corrected * 60000).toISOString();
}

// toLocalParts liefert Datum und Uhrzeit (Europe/Berlin) eines ISO-Zeitpunkts.
export function toLocalParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return { date: localDate(d), time: timeOnly.format(d) };
}

export function addDays(date: string, days: number): string {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days, 12));
  return t.toISOString().slice(0, 10);
}

// travelLabel: Fahrzeit lesbar („34 Min.“, „1 Std. 5 Min.“).
export function travelLabel(minutes?: number | null): string {
  if (minutes === null || minutes === undefined) return '';
  if (minutes < 60) return `${minutes} Min.`;
  return `${Math.floor(minutes / 60)} Std. ${minutes % 60} Min.`;
}

export function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

export const outcomeLabels: Record<CallOutcomeCode, string> = {
  APPOINTMENT: 'Termin vereinbart',
  CALLBACK: 'Rückruf',
  NOT_REACHED: 'Nicht erreicht',
  NOT_INTERESTED: 'Kein Interesse',
  WRONG_NUMBER: 'Falsche Nummer',
  WRONG_CONTACT: 'Falscher Ansprechpartner',
  CLARIFICATION: 'Klärung',
  BUYER: 'Sucht eine Immobilie',
  FOLLOW_UP: 'Wiedervorlage',
  OTHER: 'Sonstiges',
};

export const callStatusLabels: Record<CallStatus, string> = {
  CREATED: 'Wird aufgebaut',
  INITIATED: 'Ihr Telefon klingelt',
  QUEUED: 'In Warteschlange',
  RINGING: 'Es klingelt beim Kunden',
  IN_PROGRESS: 'Gespräch läuft',
  COMPLETED: 'Beendet',
  BUSY: 'Besetzt',
  NO_ANSWER: 'Keine Antwort',
  FAILED: 'Fehlgeschlagen',
  CANCELED: 'Abgebrochen',
};

export const terminalCallStatuses: CallStatus[] = ['COMPLETED', 'BUSY', 'NO_ANSWER', 'FAILED', 'CANCELED'];

export const appointmentStatusLabels: Record<AppointmentStatus, string> = {
  PLANNED: 'Geplant',
  NEEDS_CONFIRMATION: 'Außendienst offen',
  CONFIRMED: 'Bestätigt',
  RESCHEDULED: 'Verschoben',
  COMPLETED: 'Erledigt',
  CANCELED: 'Abgesagt',
  NO_SHOW: 'Nicht angetroffen',
};

// deliveryLabels: Zustellung einer E-Mail an Kunden (OFFENE_FRAGEN D-25) – nach dem Versand ausstehend, bis die
// Nachrichtenverfolgung von Microsoft sie bestätigt oder als gescheitert meldet.
export const deliveryLabels: Record<string, { text: string; badge: string }> = {
  PENDING: { text: 'Zustellung wird geprüft', badge: 'badge-info' },
  DELIVERED: { text: 'zugestellt', badge: 'badge-ok' },
  FAILED: { text: 'nicht zugestellt', badge: 'badge-error' },
  UNKNOWN: { text: 'Zustellung unklar', badge: 'badge-muted' },
};

export const tierLabels: Record<string, string> = {
  CALLBACK_DUE: 'Vereinbarter Rückruf fällig',
  DIALFIRE_APPOINTMENT: 'Frischer Termin aus Dialfire',
  FOLLOW_UP_DUE: 'Wiedervorlage fällig',
  FOLLOW_UP_TODAY: 'Heute vereinbarter Folgekontakt',
  NEW_PRIORITIZED: 'Priorisierter neuer Lead',
  NEW_FIELD_SALES: 'Neuer Lead · Gebiet mit Makler',
  RETRY_FIELD_SALES: 'Erneuter Versuch · Gebiet mit Makler',
  NEW: 'Neuer Lead',
  RETRY: 'Erneuter Versuch',
  RESUMED: 'Fortgesetzt',
  MANUAL: 'Manuell geöffnet',
};

export const fieldLabels: Record<string, string> = {
  contact_name: 'Ansprechpartner',
  company_name: 'Firma',
  phone: 'Telefon',
  email: 'E-Mail',
  address: 'Adresse',
  start_at: 'Terminzeit',
};

export const actionLabels: Record<string, string> = {
  CREATE_CALLBACK: 'Rückruf anlegen',
  CREATE_APPOINTMENT: 'Termin anlegen',
  UPDATE_CONTACT: 'Kontakt aktualisieren',
  UPDATE_ADDRESS: 'Adresse aktualisieren',
  CREATE_TASK: 'Aufgabe anlegen',
  REQUEST_REVIEW: 'Prüfung erforderlich',
};

export function personName(p: { company_name?: string | null; first_name?: string | null; last_name?: string | null }): string {
  const person = [p.first_name, p.last_name].filter(Boolean).join(' ');
  return p.company_name || person || 'Ohne Namen';
}

export function addressLine(a: { street?: string | null; house_number?: string | null; postal_code?: string | null; city?: string | null }): string {
  const line1 = [a.street, a.house_number].filter(Boolean).join(' ');
  const line2 = [a.postal_code, a.city].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ') || '–';
}

// displayValue zeigt Werte von KI-Vorschlägen (Zeichenketten oder Adress-/Namensobjekte) lesbar an.
export function displayValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v);
    return v;
  }
  if (typeof v === 'object') {
    const o = v as Record<string, string>;
    if ('street' in o || 'postal_code' in o) return addressLine(o);
    if ('first_name' in o || 'last_name' in o) return [o.first_name, o.last_name].filter(Boolean).join(' ') || '–';
    return JSON.stringify(v);
  }
  return String(v);
}
